import "./styles.css";
import type { AppState, Entity, EntityType, Entry, Section } from "./types";

type View = "overview" | "cars" | "drivers" | "shifts" | "agent" | "settings";
type Language = "es" | "en";

const app: HTMLDivElement = (() => {
  const element = document.querySelector<HTMLDivElement>("#app");
  if (!element) throw new Error("App root is missing");
  return element;
})();

let state: AppState = { entities: [], sections: [], entries: [], driverContacts: [], vehicleAssignments: [] };
const routes: Record<View, string> = { overview: "/", cars: "/cars", drivers: "/drivers", shifts: "/driver-shifts", agent: "/agent", settings: "/settings" };
const routeView = (): View => (Object.entries(routes).find(([, path]) => path === window.location.pathname)?.[0] as View | undefined) || "overview";
let view: View = routeView();
let selectedEntityId: string | null = null;
let loading = true;
let menuOpen = false;
let language: Language = localStorage.getItem("ali-taxi-language") === "en" ? "en" : "es";
document.documentElement.lang = language;

const copy: Record<string, Record<Language, string>> = {
  menu: { es: "MENÚ", en: "MENU" }, overview: { es: "Resumen", en: "Overview" }, cars: { es: "Coches", en: "Cars" }, drivers: { es: "Conductores", en: "Drivers" }, shifts: { es: "Turnos", en: "Driver shifts" }, agent: { es: "Agente", en: "Agent" }, settings: { es: "Ajustes", en: "Settings" },
  openMenu: { es: "Abrir menú", en: "Open menu" }, closeMenu: { es: "Cerrar menú", en: "Close menu" }, close: { es: "Cerrar", en: "Close" },
  readyManage: { es: "Listos para gestionar", en: "Ready to manage" }, addFirstCar: { es: "Añade tu primer coche", en: "Add your first car" }, filesActive: { es: "Fichas activas", en: "Driver files active" }, addFirstDriver: { es: "Añade tu primer conductor", en: "Add your first driver" }, recordedSpend: { es: "Gasto registrado", en: "Recorded spend" }, allExpenses: { es: "En todos los registros de gastos", en: "Across all expense records" }, recentRecords: { es: "Registros recientes", en: "Recent records" }, latest: { es: "últimos", en: "latest" },
  driversCars: { es: "Conductores y coches", en: "Drivers and cars" }, recurringShifts: { es: "Turnos diarios de 12 horas", en: "Recurring 12-hour shifts." }, driver: { es: "Conductor", en: "Driver" }, car: { es: "Coche", en: "Car" }, noRegistration: { es: "Sin matrícula", en: "No registration" }, noShifts: { es: "No hay turnos asignados", en: "No shifts assigned" }, assignCarHint: { es: "Abre un conductor y selecciona Asignar coche.", en: "Open a driver and choose Assign car." }, unassignedDrivers: { es: "Conductores sin asignar", en: "Unassigned drivers" }, tapAssign: { es: "Toca un conductor para asignarle un coche y un turno de 12 horas.", en: "Tap a driver to give them a car and 12-hour slot." },
  manageCars: { es: "Abre un coche para gestionar revisiones, ITV y mantenimiento.", en: "Open a car to manage servicing, MOT and maintenance." }, manageDrivers: { es: "Organiza trayectos, combustible y gastos por conductor.", en: "Keep rides, fuel and expenses under the right driver." }, add: { es: "Añadir", en: "Add" }, noCars: { es: "Todavía no hay coches", en: "No cars yet" }, noDrivers: { es: "Todavía no hay conductores", en: "No drivers yet" }, useButton: { es: "Usa el botón de arriba para añadir el primero.", en: "Use the button above to add your first one." }, records: { es: "registros", en: "records" }, unknown: { es: "Desconocido", en: "Unknown" }, unfiled: { es: "Sin archivar", en: "Unfiled" }, receipt: { es: "Recibo", en: "Receipt" }, noDate: { es: "Sin fecha", en: "No date" },
  dailyShift: { es: "Turno diario", en: "Daily shift" }, noCarAssigned: { es: "Sin coche asignado", en: "No car assigned" }, chooseSlot: { es: "Elige un turno recurrente de 12 horas.", en: "Choose a recurring 12-hour slot." }, everyDay: { es: "cada día", en: "every day" }, whatsappUnlinked: { es: "WhatsApp no vinculado", en: "WhatsApp not linked" }, whatsapp: { es: "WhatsApp", en: "WhatsApp" }, assignCar: { es: "Asignar coche", en: "Assign car" }, viewMap: { es: "Ver turnos", en: "View map" }, servicingCar: { es: "Revisiones, ITV y mantenimiento.", en: "Servicing, MOT and maintenance." }, driverExpenses: { es: "Trayectos, combustible y gastos.", en: "Rides, fuel and expenses." }, edit: { es: "Editar", en: "Edit" }, section: { es: "Sección", en: "Section" }, subsection: { es: "Subsección", en: "Subsection" }, record: { es: "Registro", en: "Record" }, noSections: { es: "No hay secciones", en: "No sections" }, addSectionHint: { es: "Añade una sección para empezar a organizar esta ficha.", en: "Add a section to start organising this file." },
  askAliTaxi: { es: "Pregunta a Ali Taxi", en: "Ask Ali Taxi" }, chatHint: { es: "Habla con naturalidad o adjunta un recibo.", en: "Chat naturally or attach a receipt." }, addCarPrompt: { es: "Añadir un coche", en: "Add a car" }, addDriverPrompt: { es: "Añadir conductor Jamal", en: "Add driver Jamal" }, capabilitiesPrompt: { es: "¿Qué puedes hacer?", en: "What can you do?" }, photo: { es: "Foto", en: "Photo" }, messageAgent: { es: "Escribe al agente…", en: "Message the agent…" }, send: { es: "Enviar", en: "Send" }, thingsTry: { es: "Prueba esto", en: "Things to try" }, pendingReceipt: { es: "Recibo pendiente: ", en: "Pending receipt: " }, caption: { es: "Texto: ", en: "Caption: " }, removeImage: { es: "Quitar imagen", en: "Remove image" }, attachReceipt: { es: "Adjuntar imagen del recibo", en: "Attach receipt image" },
  language: { es: "Idioma", en: "Language" }, languageHelp: { es: "Elige el idioma de la aplicación.", en: "Choose the app language." }, spanish: { es: "Español", en: "Spanish" }, english: { es: "Inglés", en: "English" }, defaultLanguage: { es: "Idioma predeterminado", en: "Default language" }, savedAutomatically: { es: "Los cambios se guardan automáticamente en este dispositivo.", en: "Changes are saved automatically on this device." }, languageChanged: { es: "Idioma actualizado", en: "Language updated" },
  opening: { es: "Abriendo la gestión de flota…", en: "Opening fleet desk…" }, thinking: { es: "Pensando…", en: "Thinking…" }, cancel: { es: "Cancelar", en: "Cancel" }, save: { es: "Guardar", en: "Save" }, name: { es: "Nombre", en: "Name" }, registration: { es: "Matrícula", en: "Registration" }, title: { es: "Título", en: "Title" }, amount: { es: "Importe (£)", en: "Amount (£)" }, date: { es: "Fecha", en: "Date" }, notes: { es: "Notas", en: "Notes" },
};

const t = (key: string): string => copy[key]?.[language] || key;

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  attachment?: string;
}

let chatMessages: ChatMessage[] = [{
  role: "assistant",
  content: language === "es" ? "Dime qué quieres organizar. Puedo añadir coches y conductores, crear secciones o leer un recibo adjunto." : "Tell me what you want to organise. I can add cars and drivers, create sections, or read an attached receipt.",
}];
let chatAttachment: File | null = null;
let chatAttachmentCaption = "";
let chatAttachmentSent = false;
let chatBusy = false;
let chatStatus = t("thinking");

const icons: Record<string, string> = {
  overview: "⌂",
  cars: "◆",
  drivers: "●",
  shifts: "⇄",
  agent: "✦",
  settings: "⚙",
};

const escapeHtml = (value: unknown): string => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

const formatChatText = (value: string): string => escapeHtml(value)
  .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
  .replace(/^\s*\*\s+/gm, "• ")
  .replaceAll("\n", "<br>");

const money = (amount: number | null, currency = "GBP") => amount === null
  ? "—"
  : new Intl.NumberFormat(language === "es" ? "es-ES" : "en-GB", { style: "currency", currency }).format(amount);

const date = (value: string | null) => value
  ? new Intl.DateTimeFormat(language === "es" ? "es-ES" : "en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(value))
  : t("noDate");

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const response = await fetch(path, { ...init, headers });
  const data = await response.json().catch(() => ({})) as { error?: string } & T;
  if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
  return data;
}

async function refresh(): Promise<void> {
  state = await api<AppState>("/api/state");
  loading = false;
  render();
}

function navButton(target: View, label: string): string {
  const active = view === target;
  return `<button class="nav-button ${active ? "active" : ""}" data-nav="${target}"><span class="nav-icon">${icons[target]}</span><span>${label}</span></button>`;
}

function shell(content: string): string {
  return `<div class="shell">
    <header class="floating-header"><button class="header-menu-button" data-toggle-menu aria-label="${t("openMenu")}" aria-expanded="${menuOpen}"><span></span></button><button class="header-brand" data-nav="overview" aria-label="${t("overview")}">ALI TAXI</button><span class="header-spacer"></span></header>
    <button class="drawer-scrim ${menuOpen ? "open" : ""}" data-toggle-menu aria-label="${t("closeMenu")}"></button>
    <aside class="sidebar ${menuOpen ? "open" : ""}">
      <div class="drawer-head"><strong>${t("menu")}</strong><button data-toggle-menu aria-label="${t("closeMenu")}">×</button></div>
      <nav class="nav">
        ${navButton("overview", t("overview"))}
        ${navButton("cars", t("cars"))}
        ${navButton("drivers", t("drivers"))}
        ${navButton("shifts", t("shifts"))}
        ${navButton("agent", t("agent"))}
        ${navButton("settings", t("settings"))}
      </nav>
    </aside>
    <main class="main">${content}</main>
  </div>`;
}

function setMenuOpen(open: boolean): void {
  menuOpen = open;
  document.querySelector(".sidebar")?.classList.toggle("open", open);
  document.querySelector(".drawer-scrim")?.classList.toggle("open", open);
  document.querySelector(".header-menu-button")?.setAttribute("aria-expanded", String(open));
}

function assignmentForDriver(driverId: string) {
  return state.vehicleAssignments.find((item) => item.driver_id === driverId);
}

function shiftMapView(): string {
  const drivers = state.entities.filter((item) => item.type === "driver");
  const assigned = drivers.flatMap((driver) => {
    const assignment = assignmentForDriver(driver.id);
    const car = assignment ? state.entities.find((item) => item.id === assignment.car_id) : null;
    return assignment && car ? [{ driver, assignment, car }] : [];
  });
  const unassigned = drivers.filter((driver) => !assignmentForDriver(driver.id));
  return shell(`${pageHeading(t("driversCars"), t("recurringShifts"))}
    <section class="shift-map">
      ${assigned.length ? assigned.map(({ driver, assignment, car }) => `<div class="shift-route">
        <span class="shift-node driver-node"><small>${t("driver")}</small><strong>${escapeHtml(driver.name)}</strong><em>${escapeHtml(assignment.start_time)}–${escapeHtml(assignment.end_time)}</em></span>
        <span class="route-track"><i></i></span>
        <span class="shift-node car-node"><small>${t("car")}</small><strong>${escapeHtml(car.name)}</strong><em>${escapeHtml(car.registration || t("noRegistration"))}</em></span>
      </div>`).join("") : empty(t("noShifts"), t("assignCarHint"))}
    </section>
    ${unassigned.length ? `<div class="section-heading"><div><h2>${t("unassignedDrivers")}</h2><p>${t("tapAssign")}</p></div></div><div class="unassigned-list">${unassigned.map((driver) => `<button class="secondary" data-open-entity="${driver.id}">${escapeHtml(driver.name)}</button>`).join("")}</div>` : ""}
  `);
}

function currentEntity(): Entity | undefined {
  return state.entities.find((item) => item.id === selectedEntityId);
}

function pageHeading(title: string, subhead = "", action = ""): string {
  return `<header class="topbar page-heading"><div><h1>${escapeHtml(title)}</h1>${subhead ? `<p class="subhead">${escapeHtml(subhead)}</p>` : ""}</div>${action}</header>`;
}

function entityCard(entity: Entity): string {
  const count = state.entries.filter((entry) => entry.entity_id === entity.id).length;
  return `<article class="card clickable"><button class="card-button" data-open-entity="${entity.id}">
    <div class="card-top"><span class="badge">${entity.type === "car" ? t("car") : t("driver")}</span><span class="chevron">›</span></div>
    <h3>${escapeHtml(entity.name)}</h3>
    <p>${entity.registration ? `<span class="registration">${escapeHtml(entity.registration)}</span>` : `${count} record${count === 1 ? "" : "s"}`}</p>
  </button></article>`;
}

function overviewView(): string {
  const cars = state.entities.filter((item) => item.type === "car");
  const drivers = state.entities.filter((item) => item.type === "driver");
  const spend = state.entries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
  const recent = [...state.entries].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 5);
  return shell(`<section class="stats overview-stats">
      <button class="stat stat-button" data-nav="cars"><span>${t("cars")}</span><strong>${cars.length}</strong><em>${cars.length ? t("readyManage") : t("addFirstCar")}</em><i>›</i></button>
      <button class="stat stat-button" data-nav="drivers"><span>${t("drivers")}</span><strong>${drivers.length}</strong><em>${drivers.length ? t("filesActive") : t("addFirstDriver")}</em><i>›</i></button>
      <button class="stat stat-button" data-open-records><span>${t("recordedSpend")}</span><strong>${money(spend)}</strong><em>${t("allExpenses")}</em><i>›</i></button>
    </section>
    <details class="recent-records"><summary><span>${t("recentRecords")}</span><small>${recent.length} ${t("latest")}</small></summary>${recent.length ? `<div class="compact-records">${recent.map(entryRow).join("")}</div>` : empty(language === "es" ? "Todavía no hay registros" : "No records yet", language === "es" ? "Los registros aparecerán aquí." : "Records will appear here.")}</details>
  `);
}

function listView(type: EntityType): string {
  const isCar = type === "car";
  const items = state.entities.filter((entity) => entity.type === type);
  return shell(`${pageHeading(isCar ? t("cars") : t("drivers"), isCar ? t("manageCars") : t("manageDrivers"), `<button class="primary" data-add-entity="${type}">＋ ${t("add")} ${isCar ? t("car").toLowerCase() : t("driver").toLowerCase()}</button>`)}
    ${items.length ? `<div class="grid">${items.map(entityCard).join("")}</div>` : empty(isCar ? t("noCars") : t("noDrivers"), t("useButton"))}
  `);
}

function entryRow(entry: Entry): string {
  const entity = state.entities.find((item) => item.id === entry.entity_id);
  const section = state.sections.find((item) => item.id === entry.section_id);
  return `<div class="card entry"><div><strong>${escapeHtml(entry.title)}</strong><small>${escapeHtml(entity?.name || t("unknown"))} · ${escapeHtml(section?.name || t("unfiled"))} · ${date(entry.event_date || entry.created_at)}${entry.receipt_key ? ` · <a class="receipt-link" href="/api/receipts/${encodeURIComponent(entry.receipt_key)}" target="_blank">${t("receipt")}</a>` : ""}</small></div><div class="amount">${money(entry.amount, entry.currency)}</div></div>`;
}

function sectionEntries(sectionId: string): Entry[] {
  return state.entries.filter((entry) => entry.section_id === sectionId);
}

function childSection(section: Section): string {
  const entries = sectionEntries(section.id);
  return `<div class="child-block"><div class="child-section"><span><strong>${escapeHtml(section.name)}</strong> <small>· ${entries.length}</small></span><span class="section-tools"><button class="tiny" data-add-entry="${section.id}">${t("add")}</button><button class="tiny" data-edit-section="${section.id}">${t("edit")}</button></span></div>${entries.length ? `<div class="child-entries">${entries.map((entry) => `<div class="entry"><div><strong>${escapeHtml(entry.title)}</strong><small>${date(entry.event_date || entry.created_at)}${entry.receipt_key ? ` · <a class="receipt-link" href="/api/receipts/${encodeURIComponent(entry.receipt_key)}" target="_blank">${t("receipt")}</a>` : ""}</small></div><div class="amount">${money(entry.amount, entry.currency)}</div></div>`).join("")}</div>` : ""}</div>`;
}

function sectionCard(section: Section): string {
  const children = state.sections.filter((item) => item.parent_id === section.id).sort((a, b) => a.sort_order - b.sort_order);
  const entries = sectionEntries(section.id);
  return `<article class="section-card">
    <div class="section-card-head"><div><h3>${escapeHtml(section.name)}</h3><small>${entries.length} ${t("records")}</small></div><div class="section-tools"><button class="tiny" data-add-entry="${section.id}">＋ ${t("record")}</button><button class="tiny" data-add-section="${section.entity_id}" data-parent-section="${section.id}">＋ ${t("subsection")}</button><button class="tiny" data-edit-section="${section.id}">${t("edit")}</button></div></div>
    ${children.length ? `<div class="children">${children.map(childSection).join("")}</div>` : ""}
    ${entries.length ? `<div class="entries">${entries.map((entry) => `<div class="entry"><div><strong>${escapeHtml(entry.title)}</strong><small>${date(entry.event_date || entry.created_at)}${entry.receipt_key ? ` · <a class="receipt-link" href="/api/receipts/${encodeURIComponent(entry.receipt_key)}" target="_blank">${t("receipt")}</a>` : ""}</small></div><div class="amount">${money(entry.amount, entry.currency)}</div></div>`).join("")}</div>` : ""}
  </article>`;
}

function entityModal(): string {
  const entity = currentEntity();
  if (!entity) return "";
  const roots = state.sections.filter((section) => section.entity_id === entity.id && !section.parent_id).sort((a, b) => a.sort_order - b.sort_order);
  const assignment = entity.type === "driver" ? assignmentForDriver(entity.id) : null;
  const assignedCar = assignment ? state.entities.find((item) => item.id === assignment.car_id) : null;
  const contact = entity.type === "driver" ? state.driverContacts.find((item) => item.driver_id === entity.id) : null;
  const carDrivers = entity.type === "car" ? state.vehicleAssignments.filter((item) => item.car_id === entity.id).map((item) => ({ assignment: item, driver: state.entities.find((entity) => entity.id === item.driver_id) })).filter((item) => item.driver) : [];
  const relationship = entity.type === "driver"
    ? `<section class="relationship-card"><div><span class="badge">${t("dailyShift")}</span><h2>${assignedCar ? `${escapeHtml(assignedCar.name)} ${assignedCar.registration ? `(${escapeHtml(assignedCar.registration)})` : ""}` : t("noCarAssigned")}</h2><p>${assignment ? `${escapeHtml(assignment.start_time)}–${escapeHtml(assignment.end_time)} ${t("everyDay")}` : t("chooseSlot")} · ${contact ? `WhatsApp +${escapeHtml(contact.whatsapp_number)}` : t("whatsappUnlinked")}</p></div><div class="action-row"><button class="secondary" data-link-whatsapp="${entity.id}">${t("whatsapp")}</button><button class="primary" data-assign-driver="${entity.id}">${t("assignCar")}</button></div></section>`
    : carDrivers.length ? `<section class="relationship-card"><div><span class="badge">${t("drivers")}</span><h2>${carDrivers.map(({ driver }) => escapeHtml(driver?.name || "")).join(" + ")}</h2><p>${carDrivers.map(({ driver, assignment }) => `${escapeHtml(driver?.name || "")}: ${escapeHtml(assignment.start_time)}–${escapeHtml(assignment.end_time)}`).join(" · ")}</p></div><button class="secondary" data-nav="shifts">${t("viewMap")}</button></section>` : "";
  return `<dialog class="entity-dialog" id="entity-dialog"><div class="entity-dialog-inner"><button class="entity-dialog-close" data-close-entity aria-label="${t("close")}">×</button>
    ${pageHeading(entity.name, entity.type === "car" ? t("servicingCar") : t("driverExpenses"), `<div class="action-row">${entity.registration ? `<span class="registration">${escapeHtml(entity.registration)}</span>` : ""}<button class="secondary" data-edit-entity="${entity.id}">${t("edit")}</button><button class="primary" data-add-section="${entity.id}">＋ ${t("section")}</button></div>`)}
    ${relationship}
    <div class="section-tree">${roots.length ? roots.map(sectionCard).join("") : empty(t("noSections"), t("addSectionHint"))}</div>
  </div></dialog>`;
}

function agentView(): string {
  const messages = chatMessages.map((message) => `<div class="chat-row ${message.role}"><div class="chat-avatar">${message.role === "assistant" ? "AT" : language === "es" ? "Tú" : "You"}</div><div class="chat-bubble">${message.attachment ? `<span class="chat-attachment">▧ ${escapeHtml(message.attachment)}</span>` : ""}<p>${formatChatText(message.content)}</p></div></div>`).join("");
  return shell(`${pageHeading(t("askAliTaxi"), t("chatHint"))}
    <div class="agent-panel">
      <section class="chat-card">
        <div class="chat-stream" id="chat-stream" aria-live="polite">${messages}${chatBusy ? `<div class="chat-row assistant transient"><div class="chat-avatar">AT</div><div class="chat-bubble typing"><span>${escapeHtml(chatStatus)}</span><span class="thinking-dots"><i></i><i></i><i></i></span></div></div>` : ""}</div>
        <div class="chat-suggestions"><button type="button" data-chat-prompt="${t("addCarPrompt")}">${t("addCarPrompt")}</button><button type="button" data-chat-prompt="${t("addDriverPrompt")}">${t("addDriverPrompt")}</button><button type="button" data-chat-prompt="${t("capabilitiesPrompt")}">${t("capabilitiesPrompt")}</button></div>
        <form class="chat-composer" id="chat-form">
          ${chatAttachment ? `<div class="selected-file"><span>▧ ${chatAttachmentSent ? t("pendingReceipt") : ""}${escapeHtml(chatAttachment.name)}${chatAttachmentCaption ? `<small>${t("caption")}${escapeHtml(chatAttachmentCaption)}</small>` : ""}</span><button type="button" data-remove-chat-image aria-label="${t("removeImage")}">×</button></div>` : ""}
          <div class="composer-row"><label class="attach-button" for="chat-image" title="${t("attachReceipt")}">＋ ${t("photo")}</label><input id="chat-image" type="file" accept="image/jpeg,image/png,image/webp" hidden><textarea id="chat-message" rows="1" placeholder="${t("messageAgent")}" aria-label="${t("messageAgent")}"></textarea><button class="primary send-button" type="submit"${chatBusy ? " disabled" : ""}>${t("send")}</button></div>
        </form>
      </section>
      <aside class="help-card"><h3>${t("thingsTry")}</h3><ul><li>${language === "es" ? "“Añade coche Mercedes E-Class, AB12 CDE”" : "“Add car Mercedes E-Class, AB12 CDE”"}</li><li>${language === "es" ? "“Añade al conductor Jamal”" : "“Add driver Jamal”"}</li><li>${language === "es" ? "“Crea una sección Neumáticos para AB12 CDE”" : "“Create a Tyres section for AB12 CDE”"}</li><li>${language === "es" ? "Adjunta un recibo y escribe “Jamal, gasolina”" : "Attach a receipt and say “Jamal, petrol spent”"}</li></ul><p>${language === "es" ? "WhatsApp utiliza la misma lógica de archivo mediante" : "WhatsApp uses the same filing logic through"} <code>/webhooks/whatsapp</code>.</p></aside>
    </div>
  `);
}

function settingsView(): string {
  return shell(`${pageHeading(t("settings"), t("languageHelp"))}
    <section class="settings-card">
      <div class="settings-copy"><span class="settings-icon">文</span><div><h2>${t("language")}</h2><p>${t("savedAutomatically")}</p></div></div>
      <div class="language-options" role="radiogroup" aria-label="${t("language")}">
        <label class="language-option ${language === "es" ? "selected" : ""}"><input type="radio" name="language" value="es" ${language === "es" ? "checked" : ""}><span><strong>Español</strong><small>${t("defaultLanguage")}</small></span></label>
        <label class="language-option ${language === "en" ? "selected" : ""}"><input type="radio" name="language" value="en" ${language === "en" ? "checked" : ""}><span><strong>English</strong><small>${t("english")}</small></span></label>
      </div>
    </section>`);
}

function empty(title: string, description: string): string {
  return `<div class="empty"><strong>${escapeHtml(title)}</strong>${escapeHtml(description)}</div>`;
}

function render(): void {
  if (loading) {
    app.innerHTML = `<div class="loading"><div><div class="spinner"></div>${t("opening")}</div></div>`;
    return;
  }
  const content = view === "overview" ? overviewView()
    : view === "cars" ? listView("car")
      : view === "drivers" ? listView("driver")
        : view === "shifts" ? shiftMapView()
          : view === "agent" ? agentView()
            : settingsView();
  app.innerHTML = content + entityModal();
  const dialog = document.querySelector<HTMLDialogElement>("#entity-dialog");
  if (dialog && !dialog.open) dialog.showModal();
}

function showToast(message: string, isError = false): void {
  document.querySelector(".toast")?.remove();
  const toast = document.createElement("div");
  toast.className = `toast${isError ? " error" : ""}`;
  toast.textContent = message;
  document.body.append(toast);
  window.setTimeout(() => toast.remove(), 4200);
}

function openDialog(title: string, fields: string, onSubmit: (form: FormData) => Promise<void>, submitLabel = t("save")): void {
  const dialog = document.createElement("dialog");
  dialog.innerHTML = `<form class="dialog-inner" method="dialog"><div class="dialog-head"><h2>${escapeHtml(title)}</h2><button class="close" type="button" aria-label="${t("close")}">×</button></div>${fields}<div class="dialog-actions"><button class="secondary" type="button" data-cancel>${t("cancel")}</button><button class="primary" type="submit">${escapeHtml(submitLabel)}</button></div></form>`;
  document.body.append(dialog);
  const close = () => { dialog.close(); dialog.remove(); };
  dialog.querySelector(".close")?.addEventListener("click", close);
  dialog.querySelector("[data-cancel]")?.addEventListener("click", close);
  dialog.addEventListener("close", () => dialog.remove());
  dialog.querySelector("form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const submit = dialog.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.disabled = true;
    try { await onSubmit(new FormData(event.currentTarget as HTMLFormElement)); close(); }
    catch (error) { showToast(error instanceof Error ? error.message : "Something went wrong", true); if (submit) submit.disabled = false; }
  });
  dialog.showModal();
}

function addEntity(type: EntityType): void {
  const entityLabel = type === "car" ? t("car") : t("driver");
  openDialog(`${t("add")} ${entityLabel.toLowerCase()}`, `<div class="field"><label for="entity-name">${t("name")}</label><input id="entity-name" name="name" required placeholder="${type === "car" ? "Mercedes E-Class" : language === "es" ? "Nombre del conductor" : "Driver name"}"></div>${type === "car" ? `<div class="field"><label for="entity-registration">${t("registration")}</label><input id="entity-registration" name="registration" required placeholder="AB12 CDE" autocapitalize="characters"></div>` : ""}`, async (form) => {
    await api("/api/entities", { method: "POST", body: JSON.stringify({ type, name: form.get("name"), registration: form.get("registration") || null }) });
    await refresh();
    showToast(type === "car" ? (language === "es" ? "Coche añadido" : "Car added") : (language === "es" ? "Conductor añadido" : "Driver added"));
  }, `${t("add")} ${entityLabel.toLowerCase()}`);
}

function editEntity(entity: Entity): void {
  openDialog(`${t("edit")} ${(entity.type === "car" ? t("car") : t("driver")).toLowerCase()}`, `<div class="field"><label for="entity-name">${t("name")}</label><input id="entity-name" name="name" required value="${escapeHtml(entity.name)}"></div>${entity.type === "car" ? `<div class="field"><label for="entity-registration">${t("registration")}</label><input id="entity-registration" name="registration" required value="${escapeHtml(entity.registration || "")}" autocapitalize="characters"></div>` : ""}`, async (form) => {
    await api(`/api/entities/${entity.id}`, { method: "PATCH", body: JSON.stringify({ name: form.get("name"), registration: form.get("registration") || null }) });
    await refresh(); showToast(language === "es" ? "Detalles actualizados" : "Details updated");
  });
}

function assignDriver(driver: Entity): void {
  const cars = state.entities.filter((item) => item.type === "car");
  if (!cars.length) { showToast(language === "es" ? "Añade un coche primero" : "Add a car first", true); return; }
  const current = assignmentForDriver(driver.id);
  const carOptions = cars.map((car) => `<option value="${car.id}"${current?.car_id === car.id ? " selected" : ""}>${escapeHtml(car.name)}${car.registration ? ` · ${escapeHtml(car.registration)}` : ""}</option>`).join("");
  const slots = [["00:00", "12:00"], ["06:00", "18:00"], ["12:00", "00:00"], ["18:00", "06:00"]];
  const slotOptions = slots.map(([start, end]) => `<option value="${start}|${end}"${current?.start_time === start && current?.end_time === end ? " selected" : ""}>${start}–${end}</option>`).join("");
  openDialog(`${t("assignCar")}: ${driver.name}`, `<div class="field"><label for="shift-car">${t("car")}</label><select id="shift-car" name="car_id">${carOptions}</select></div><div class="field"><label for="shift-time">${language === "es" ? "Turno diario de 12 horas" : "Daily 12-hour slot"}</label><select id="shift-time" name="slot">${slotOptions}</select></div>`, async (form) => {
    const [start_time, end_time] = String(form.get("slot") || "").split("|");
    await api("/api/assignments", { method: "POST", body: JSON.stringify({ driver_id: driver.id, car_id: form.get("car_id"), start_time, end_time }) });
    await refresh(); showToast(language === "es" ? "Turno guardado" : "Daily car shift saved");
  }, language === "es" ? "Guardar turno" : "Save shift");
}

function linkWhatsApp(driver: Entity): void {
  const current = state.driverContacts.find((item) => item.driver_id === driver.id)?.whatsapp_number || "";
  openDialog(language === "es" ? `Vincular WhatsApp de ${driver.name}` : `Link ${driver.name}’s WhatsApp`, `<div class="field"><label for="whatsapp-number">${language === "es" ? "Número con código de país" : "Number including country code"}</label><input id="whatsapp-number" name="whatsapp_number" inputmode="tel" required value="${escapeHtml(current)}" placeholder="447700900123"></div><p class="dialog-note">${language === "es" ? "Los mensajes de este número pueden usar textos cortos como “black” o “gasolina”." : "Messages from this number can use short captions such as “black” or “petrol”."}</p>`, async (form) => {
    await api("/api/driver-contacts", { method: "POST", body: JSON.stringify({ driver_id: driver.id, whatsapp_number: form.get("whatsapp_number") }) });
    await refresh(); showToast(language === "es" ? "WhatsApp vinculado al conductor" : "WhatsApp linked to driver");
  }, language === "es" ? "Vincular número" : "Link number");
}

function addSection(entityId: string, parentId: string | null): void {
  openDialog(`${t("add")} ${parentId ? t("subsection").toLowerCase() : t("section").toLowerCase()}`, `<div class="field"><label for="section-name">${t("name")}</label><input id="section-name" name="name" required placeholder="${language === "es" ? "p. ej. Neumáticos" : "e.g. Tyres"}"></div>`, async (form) => {
    await api("/api/sections", { method: "POST", body: JSON.stringify({ entity_id: entityId, parent_id: parentId, name: form.get("name") }) });
    await refresh(); showToast(language === "es" ? "Sección añadida" : "Section added");
  }, `${t("add")} ${t("section").toLowerCase()}`);
}

function editSection(section: Section): void {
  openDialog(`${t("edit")} ${t("section").toLowerCase()}`, `<div class="field"><label for="section-name">${t("name")}</label><input id="section-name" name="name" required value="${escapeHtml(section.name)}"></div>`, async (form) => {
    await api(`/api/sections/${section.id}`, { method: "PATCH", body: JSON.stringify({ name: form.get("name") }) });
    await refresh(); showToast(language === "es" ? "Sección actualizada" : "Section updated");
  });
}

function addEntry(section: Section): void {
  openDialog(`${t("add")}: ${section.name}`, `<div class="field"><label for="entry-title">${t("title")}</label><input id="entry-title" name="title" required placeholder="${language === "es" ? "¿Para qué fue?" : "What was this for?"}"></div><div class="field"><label for="entry-amount">${t("amount")}</label><input id="entry-amount" name="amount" type="number" min="0" step="0.01" placeholder="0.00"></div><div class="field"><label for="entry-date">${t("date")}</label><input id="entry-date" name="event_date" type="date"></div><div class="field"><label for="entry-notes">${t("notes")}</label><textarea id="entry-notes" name="notes"></textarea></div>`, async (form) => {
    await api("/api/entries", { method: "POST", body: JSON.stringify({ entity_id: section.entity_id, section_id: section.id, title: form.get("title"), amount: form.get("amount") || null, event_date: form.get("event_date") || null, notes: form.get("notes") || null }) });
    await refresh(); showToast(language === "es" ? "Registro añadido" : "Record added");
  }, `${t("add")} ${t("record").toLowerCase()}`);
}

function scrollChat(): void {
  window.requestAnimationFrame(() => {
    const stream = document.querySelector<HTMLElement>("#chat-stream");
    if (stream) stream.scrollTop = stream.scrollHeight;
  });
}

async function sendAgentMessage(rawMessage: string): Promise<void> {
  const message = rawMessage.trim();
  const attachment = chatAttachment;
  const isFirstAttachmentSend = Boolean(attachment && !chatAttachmentSent);
  if (chatBusy || (!message && !attachment)) return;
  const history = chatMessages.slice(-8).map(({ role, content }) => ({ role, content }));
  if (isFirstAttachmentSend) chatAttachmentCaption = message;
  chatMessages.push({ role: "user", content: message || "Attached an image.", attachment: isFirstAttachmentSend ? attachment?.name : undefined });
  chatBusy = true;
  chatStatus = t("thinking");
  render();
  scrollChat();

  const body = new FormData();
  body.set("message", message);
  body.set("history", JSON.stringify(history));
  if (attachment) {
    body.set("image", attachment);
    body.set("attachment_caption", chatAttachmentCaption);
  }
  try {
    const response = await fetch("/api/agent/chat", { method: "POST", body });
    if (!response.ok) {
      const data = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(data.error || `Request failed (${response.status})`);
    }
    if (!response.body) throw new Error("The agent returned no response.");
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let finalResult: { message: string; changed: boolean; attachmentConsumed: boolean } | null = null;
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (!line.trim()) continue;
        const event = JSON.parse(line) as { type?: string; message?: string; changed?: boolean; attachmentConsumed?: boolean };
        if (event.type === "status" && event.message) {
          chatStatus = event.message;
          render();
          scrollChat();
        } else if (event.type === "final" && event.message) {
          finalResult = { message: event.message, changed: Boolean(event.changed), attachmentConsumed: Boolean(event.attachmentConsumed) };
        } else if (event.type === "error") {
          throw new Error(event.message || "The agent could not complete that request.");
        }
      }
      if (done) break;
    }
    if (!finalResult) throw new Error("The agent did not finish its response.");
    if (finalResult.changed) state = await api<AppState>("/api/state");
    if (finalResult.attachmentConsumed) {
      chatAttachment = null;
      chatAttachmentCaption = "";
      chatAttachmentSent = false;
    } else if (attachment) {
      chatAttachmentSent = true;
    }
    chatMessages.push({ role: "assistant", content: finalResult.message });
  } catch (error) {
    chatMessages.push({ role: "assistant", content: error instanceof Error ? error.message : "Something went wrong. Please try again." });
  } finally {
    chatBusy = false;
    render();
    scrollChat();
  }
}

app.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  if (target instanceof HTMLDialogElement && target.classList.contains("entity-dialog")) { selectedEntityId = null; render(); return; }
  const nav = target.closest<HTMLElement>("[data-nav]")?.dataset.nav as View | undefined;
  if (nav) { view = nav; menuOpen = false; selectedEntityId = null; window.history.pushState({}, "", routes[nav]); render(); return; }
  if (target.closest("[data-open-records]")) {
    const details = document.querySelector<HTMLDetailsElement>(".recent-records");
    if (details) { details.open = true; details.scrollIntoView({ behavior: "smooth", block: "start" }); }
    return;
  }
  if (target.closest("[data-toggle-menu]")) { setMenuOpen(!menuOpen); return; }
  if (target.closest("[data-close-entity]")) { selectedEntityId = null; render(); return; }
  const openId = target.closest<HTMLElement>("[data-open-entity]")?.dataset.openEntity;
  if (openId) { selectedEntityId = openId; render(); return; }
  const addType = target.closest<HTMLElement>("[data-add-entity]")?.dataset.addEntity as EntityType | undefined;
  if (addType) { addEntity(addType); return; }
  const editEntityId = target.closest<HTMLElement>("[data-edit-entity]")?.dataset.editEntity;
  if (editEntityId) { const entity = state.entities.find((item) => item.id === editEntityId); if (entity) editEntity(entity); return; }
  const assignDriverId = target.closest<HTMLElement>("[data-assign-driver]")?.dataset.assignDriver;
  if (assignDriverId) { const driver = state.entities.find((item) => item.id === assignDriverId); if (driver) assignDriver(driver); return; }
  const linkDriverId = target.closest<HTMLElement>("[data-link-whatsapp]")?.dataset.linkWhatsapp;
  if (linkDriverId) { const driver = state.entities.find((item) => item.id === linkDriverId); if (driver) linkWhatsApp(driver); return; }
  const addSectionEl = target.closest<HTMLElement>("[data-add-section]");
  if (addSectionEl?.dataset.addSection) { addSection(addSectionEl.dataset.addSection, addSectionEl.dataset.parentSection || null); return; }
  const editSectionId = target.closest<HTMLElement>("[data-edit-section]")?.dataset.editSection;
  if (editSectionId) { const section = state.sections.find((item) => item.id === editSectionId); if (section) editSection(section); return; }
  const addEntryId = target.closest<HTMLElement>("[data-add-entry]")?.dataset.addEntry;
  if (addEntryId) { const section = state.sections.find((item) => item.id === addEntryId); if (section) addEntry(section); return; }
  const prompt = target.closest<HTMLElement>("[data-chat-prompt]")?.dataset.chatPrompt;
  if (prompt) { void sendAgentMessage(prompt); return; }
  if (target.closest("[data-remove-chat-image]")) { chatAttachment = null; chatAttachmentCaption = ""; chatAttachmentSent = false; render(); scrollChat(); }
});

app.addEventListener("cancel", (event) => {
  if (!(event.target instanceof HTMLDialogElement) || !event.target.classList.contains("entity-dialog")) return;
  event.preventDefault();
  selectedEntityId = null;
  render();
});

window.addEventListener("popstate", () => {
  view = routeView();
  selectedEntityId = null;
  menuOpen = false;
  render();
});

app.addEventListener("change", (event) => {
  const input = event.target as HTMLInputElement;
  if (input.name === "language" && (input.value === "es" || input.value === "en")) {
    language = input.value;
    localStorage.setItem("ali-taxi-language", language);
    document.documentElement.lang = language;
    chatStatus = t("thinking");
    render();
    showToast(t("languageChanged"));
    return;
  }
  if (input.id !== "chat-image") return;
  const file = input.files?.[0] || null;
  if (file && file.size > 10 * 1024 * 1024) { showToast("Upload exceeds the 10 MB limit", true); return; }
  chatAttachment = file;
  chatAttachmentCaption = "";
  chatAttachmentSent = false;
  render();
  scrollChat();
});

app.addEventListener("submit", async (event) => {
  const form = event.target as HTMLFormElement;
  if (form.id !== "chat-form") return;
  event.preventDefault();
  const message = form.querySelector<HTMLTextAreaElement>("#chat-message")?.value || "";
  await sendAgentMessage(message);
});

render();
refresh().catch((error) => { loading = false; render(); showToast(error instanceof Error ? error.message : "Could not load app", true); });
